import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SparklesIcon, Bars3Icon, XMarkIcon, UserCircleIcon } from '@heroicons/react/24/outline';
import { companyApi } from '../../services/api';
import ConfirmDialog from '../ConfirmDialog';

const NAV_LINKS = [
  { label: '创建流程', id: 'steps' },
  { label: '功能模块', id: 'management' },
  { label: '技术优势', id: 'tech' },
];

const LandingNavbar: React.FC = () => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [hasCompany, setHasCompany] = useState<boolean | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);

  // 检查用户是否有主体
  useEffect(() => {
    if (isLoggedIn) {
      const checkCompany = async () => {
        try {
          const response = await companyApi.getCompanies();
          setHasCompany(response.data && response.data.length > 0);
        } catch (e) {
          setHasCompany(false);
        }
      };
      checkCompany();
      
      // 监听主体更新事件，刷新主体状态
      const handleCompanyUpdate = () => {
        checkCompany();
      };
      window.addEventListener('company:updated', handleCompanyUpdate);
      
      return () => {
        window.removeEventListener('company:updated', handleCompanyUpdate);
      };
    }
  }, [isLoggedIn]);

  const handleCreateProject = () => {
    if (hasCompany === false) {
      setShowConfirm(true);
    } else {
      navigate('/create');
    }
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const checkAuth = () => {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          setIsLoggedIn(true);
          setDisplayName(user.display_name || user.username || '');
        } catch {
          setIsLoggedIn(false);
          setDisplayName('');
        }
      } else {
        setIsLoggedIn(false);
        setDisplayName('');
      }
    };

    checkAuth();
    window.addEventListener('user:updated', checkAuth);
    window.addEventListener('storage', checkAuth);
    return () => {
      window.removeEventListener('user:updated', checkAuth);
      window.removeEventListener('storage', checkAuth);
    };
  }, []);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'shadow-xl shadow-black/20' : ''
      }`}
      style={{
        background: scrolled ? 'rgba(15, 23, 42, 0.95)' : 'transparent',
        backdropFilter: scrolled ? 'blur(16px)' : 'none',
      }}
    >
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <div
          className="flex items-center gap-2.5 cursor-pointer select-none"
          onClick={() => navigate('/')}
        >
          <div
            className="w-8 h-8 rounded-xl flex items-center justify-center"
            style={{
              background: 'linear-gradient(135deg, #3b82f6, #06b6d4)',
              boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)',
            }}
          >
            <SparklesIcon className="w-4 h-4 text-white" />
          </div>
          <span className="text-white font-bold text-base tracking-tight">智能标书助手</span>
        </div>

        <nav className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map(({ label, id }) => (
            <button
              key={id}
              onClick={() => scrollTo(id)}
              className="text-sm text-slate-400 hover:text-white transition-colors duration-200"
            >
              {label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          {isLoggedIn ? (
            <>
              <div className="hidden sm:flex items-center gap-1.5 text-sm text-slate-400 mr-1">
                <UserCircleIcon className="w-4 h-4" />
                <span className="max-w-[80px] truncate">{displayName}</span>
              </div>
              <button
                onClick={() => navigate('/projects')}
                className="hidden sm:block text-sm text-slate-400 hover:text-white transition-colors duration-200 px-3 py-1.5"
              >
                我的项目
              </button>
              <button
                onClick={handleCreateProject}
                className="px-4 py-2 text-white text-sm font-semibold rounded-xl transition-all duration-200 hover:scale-[1.02]"
                style={{
                  background: 'linear-gradient(135deg, #2563eb, #0369a1)',
                  boxShadow: '0 4px 14px rgba(37, 99, 235, 0.35)',
                }}
              >
                新建标书
              </button>
            </>
          ) : (
            <button
              onClick={() => navigate('/login')}
              className="px-5 py-2 text-white text-sm font-semibold rounded-xl transition-all duration-200 hover:scale-[1.02]"
              style={{
                background: 'linear-gradient(135deg, #2563eb, #0369a1)',
                boxShadow: '0 4px 14px rgba(37, 99, 235, 0.35)',
              }}
            >
              快速开始
            </button>
          )}
          <button
            className="md:hidden text-slate-400 hover:text-white p-1 transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <XMarkIcon className="w-5 h-5" /> : <Bars3Icon className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div
          className="md:hidden border-t border-slate-800 px-6 py-4 space-y-1"
          style={{ background: 'rgba(15, 23, 42, 0.97)', backdropFilter: 'blur(16px)' }}
        >
          {NAV_LINKS.map(({ label, id }) => (
            <button
              key={id}
              onClick={() => scrollTo(id)}
              className="block w-full text-left text-slate-300 hover:text-white text-sm py-2.5 transition-colors"
            >
              {label}
            </button>
          ))}
          {isLoggedIn ? (
            <>
              <button
                onClick={() => { navigate('/projects'); setMobileOpen(false); }}
                className="block w-full text-left text-slate-300 hover:text-white text-sm py-2.5 transition-colors"
              >
                我的项目
              </button>
              <div className="pt-2">
                <button
                  onClick={() => { handleCreateProject(); setMobileOpen(false); }}
                  className="w-full py-3 text-white text-sm font-semibold rounded-xl"
                  style={{ background: 'linear-gradient(135deg, #2563eb, #0369a1)' }}
                >
                  新建标书
                </button>
              </div>
            </>
          ) : (
            <div className="pt-2">
              <button
                onClick={() => { navigate('/login'); setMobileOpen(false); }}
                className="w-full py-3 text-white text-sm font-semibold rounded-xl"
                style={{ background: 'linear-gradient(135deg, #2563eb, #0369a1)' }}
              >
                快速开始
              </button>
            </div>
          )}
        </div>
      )}

      {/* 主体缺失确认弹框 */}
      <ConfirmDialog
        isOpen={showConfirm}
        title="需要设置投标主体"
        message="创建标书需要先设置投标主体信息。您当前还没有主体信息，建议先创建主体后再进行标书创建。"
        confirmText="去创建主体"
        cancelText="去创建标书"
        confirmButtonClass="bg-blue-600 hover:bg-blue-700 focus:ring-blue-500"
        onConfirm={() => {
          setShowConfirm(false);
          navigate('/companies/new');
        }}
        onCancel={() => {
          setShowConfirm(false);
          navigate('/create');
        }}
      />
    </header>
  );
};

export default LandingNavbar;
